import { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';

function useCountUp(target: number, active: boolean, durationMs = 1400) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!active) {
      setValue(target);
      return;
    }
    setValue(0);
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const progress = Math.min((now - start) / durationMs, 1);
      const eased = 1 - (1 - progress) ** 3;
      setValue(Math.round(target * eased));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active, target, durationMs]);

  return value;
}

function StatBlock({
  display,
  label,
  delay,
  inView,
  reducedMotion,
}: {
  display: string;
  label: string;
  delay: number;
  inView: boolean;
  reducedMotion: boolean;
}) {
  return (
    <motion.div
      className="flex flex-col items-center justify-center text-center"
      initial={reducedMotion ? false : { opacity: 0, y: 12 }}
      animate={inView ? { opacity: 1, y: 0 } : undefined}
      transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      <div
        className="font-display font-bold text-2xl md:text-3xl bg-clip-text text-transparent tabular-nums"
        style={{ backgroundImage: 'linear-gradient(135deg, #7c3aed, #0d9488)' }}
      >
        {display}
      </div>
      <div className="text-xs text-muted-foreground mt-2 leading-snug">{label}</div>
    </motion.div>
  );
}

const COMPLIANCE_BADGES = [
  { key: 'gdpr', labelKey: 'marketing.stats.badgeGdpr', className: 'border-border text-muted-foreground' },
  { key: 'aiact', labelKey: 'marketing.stats.badgeAiAct', className: 'border-border text-muted-foreground' },
  {
    key: 'human',
    labelKey: 'marketing.stats.badgeHumanLoop',
    className: 'border-brand-accent/40 bg-brand-accent/5 text-brand-accent font-medium',
  },
] as const;

/** GDPR / EU AI Act / Human in the loop — above "How it works in practice". */
export function MarketingComplianceBadges() {
  const { t } = useLanguage();
  return (
    <div className="container flex flex-wrap gap-2 justify-center pt-10 md:pt-12 pb-10 md:pb-14">
      {COMPLIANCE_BADGES.map((b) => (
        <span
          key={b.key}
          className={cn(
            'text-[11px] px-2.5 py-1 rounded-full border font-medium',
            b.className,
          )}
        >
          {t(b.labelKey)}
        </span>
      ))}
    </div>
  );
}

export function MarketingStatsBar() {
  const { t, language } = useLanguage();
  const sectionRef = useRef<HTMLElement>(null);
  const inView = useInView(sectionRef, { once: true, margin: '-60px 0px' });
  const reducedMotion = useReducedMotion() ?? false;
  const animate = inView && !reducedMotion;

  const pct = useCountUp(75, animate);
  const mult = useCountUp(3, animate);
  const days = useCountUp(7, animate);

  const trialSuffix = language === 'it' ? 'gg' : 'd';

  return (
    <section ref={sectionRef} className="border-y border-border/60 bg-background">
      <div className="container py-10 md:py-12">
        <div className="grid grid-cols-3 gap-4 sm:gap-8 md:gap-12 w-full max-w-3xl mx-auto items-center">
          <StatBlock
            display={reducedMotion ? '−75%' : `−${pct}%`}
            label={t('marketing.stats.screening')}
            delay={0}
            inView={inView}
            reducedMotion={reducedMotion}
          />
          <StatBlock
            display={reducedMotion ? '3×' : `${mult}×`}
            label={t('marketing.stats.quality')}
            delay={0.08}
            inView={inView}
            reducedMotion={reducedMotion}
          />
          <StatBlock
            display={reducedMotion ? `7${trialSuffix}` : `${days}${trialSuffix}`}
            label={t('marketing.stats.trial')}
            delay={0.16}
            inView={inView}
            reducedMotion={reducedMotion}
          />
        </div>
      </div>
    </section>
  );
}
